<template>
  <div>
    <select
      :ref="initRef"
      v-model="model"
      v-show="instance === null"
      :disabled="instance ? false : actualDisabled"
    />
  </div>
</template>
<script setup>
/*

Just select tag w/ typeahead support,
model is a primitive value as defined in "keys" prop

*/

import { computed, inject, nextTick, onUnmounted, ref, shallowRef, watch } from 'vue'
import Choices from 'choices.js'
import { useI18n } from 'vue-i18n'
import { dropdownLabel, namedEntityLabel } from '@/lib/dropdownLabel.js'

const { t, locale } = useI18n()

const model = defineModel()
const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  keys: {
    type: String,
    default: 'id',
  },
  values: {
    type: [String, Function],
    default: 'name',
  },
  shouldSelect: {
    type: Function,
    default: x => Number(x),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autoSort: {
    type: Boolean,
    default: true,
  },
  addItemText: {
    type: String,
  },
  addItemCallback: {
    type: Function,
  },
  ignoreFormEditable: { type: Boolean, default: false },
})

const actualDisabled = computed(() => {
  if (props.ignoreFormEditable) return props.disabled ?? false
  if (formEditable !== null) return !formEditable.value || props.disabled
  return props.disabled
})

const addItemKey = '__addItem'

const instance = ref(null)
const elem = ref(null)
/** Choices при setChoices/setChoiceByValue шлёт change → model watch; держим busy до nextTick */
let setallBusy = false

function beginBusy() {
  setallBusy = true
}

function endBusy() {
  nextTick(() => {
    setallBusy = false
  })
}

const initRef = el => {
  if (!instance.value) {
    instance.value = new Choices(el, {
      allowHTML: true,
      shouldSort: props.autoSort,
      resetScrollPosition: false,
      searchEnabled: true,
      searchResultLimit: -1,
      searchFields: ['__label', 'label', 'value', 'value_en', 'name', 'name_eng'],
      fuseOptions: {
        shouldSort: true,
        findAllMatches: true,
        ignoreLocation: true,
        threshold: 0.4,
      },
      sorter: (a, b) => {
        if (a.value == addItemKey || b.value == addItemKey) {
          return -1
        }

        return a.label > b.label ? 1 : -1
      },
    })

    el.addEventListener('choice', onChoice)
    elem.value = el
    setall()
    if (actualDisabled.value) {
      instance.value.disable()
    } else {
      instance.value.enable()
    }
  }
}

const formEditable = inject('form:editable', () => shallowRef(true), true)
onUnmounted(() => {
  instance.value.destroy()
  elem.value.removeEventListener('choice', onChoice)
})

watch(
  () => props.options,
  () => setall(),
  { immediate: true }
)
// Только синхронизация выбранного значения — не полный rebuild (иначе цикл с Choices)
watch(model, () => syncSelection())
watch(locale, () => setall())
watch(
  actualDisabled,
  disabled => {
    if (!instance.value) {
      return
    }

    const nativeElement = instance.value.passedElement.element
    if (nativeElement) {
      nativeElement.disabled = disabled
    }

    if (disabled) {
      instance.value.disable()
    } else {
      instance.value.enable()
    }
  },
  { immediate: true }
)

function resolveLabelFn() {
  if (props.values instanceof Function) {
    return props.values
  }
  if (props.values === 'value') {
    return item => dropdownLabel(item, locale.value)
  }
  if (props.values === 'name') {
    return item => namedEntityLabel(item, locale.value)
  }
  return null
}

function currentSelectedValue() {
  if (model.value == null || model.value === '') return null
  const selected = props.shouldSelect(model.value)
  if (selected == null || selected === '') return null
  return selected
}

function syncSelection() {
  if (!instance.value || setallBusy) {
    return
  }
  beginBusy()
  try {
    const selected = currentSelectedValue()
    const active = instance.value.getValue(true)
    const activeVal = Array.isArray(active) ? active[0] : active
    if (selected != null) {
      if (String(activeVal ?? '') === String(selected)) {
        return
      }
      instance.value.setChoiceByValue(selected)
    } else if (activeVal != null && activeVal !== '') {
      instance.value.removeActiveItems()
    }
  } finally {
    endBusy()
  }
}

function setall() {
  if (!instance.value || setallBusy) {
    return
  }
  beginBusy()
  try {
    let options = [...props.options]
    let values = props.values

    const labelFn = resolveLabelFn()
    if (labelFn) {
      values = '__label'
      options = options.map(o => ({
        id: o.id,
        name: o.name,
        name_eng: o.name_eng,
        value: o.value,
        value_en: o.value_en,
        label: o.label,
        key: o.key,
        parent_id: o.parent_id,
        [values]: labelFn(o),
      }))
    }
    if (props.addItemCallback instanceof Function) {
      let label = props.addItemText || t('add')
      options.unshift({
        [props.keys]: addItemKey,
        [values]: label,
      })
    }
    instance.value.clearStore()
    instance.value.setChoices(options, props.keys, values, true)
    const selected = currentSelectedValue()
    if (selected != null) {
      instance.value.setChoiceByValue(selected)
    } else {
      instance.value.removeActiveItems()
    }
    if (actualDisabled.value) {
      instance.value.disable()
    } else {
      instance.value.enable()
    }
  } finally {
    endBusy()
  }
}

function onChoice({ detail }) {
  if (detail.value == addItemKey) {
    setall()
    props.addItemCallback()
  }
}

</script>

<i18n>
{
  "ru": {
    "add": "Добавить"
  },
  "en": {
    "add": "Add"
  }
}
</i18n>
